import { Type, Static } from '@sinclair/typebox';
import { IsoDateTimeSchema } from '../../shared/utils/date-time.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { SendMessageAttachmentsSchema } from './message-attachment-request.schema';

export const SendMessageBaseBodySchema = Type.Object(
  {
    text: Type.String({
      description: `The textual content of first message to be sent. It's recommended not to use HTML or Markdown. To mention a chat participant, write \`@\` followed by their user ID, as found in \`sender_id\` or the chat attendees: it is replaced with their name and notifies them where the provider supports mentions. A provider may still deliver the mention as plain text.`,
    }),
    quote_id: Type.Optional(
      Type.String({
        minLength: 1,
        description: 'The ID of the Message to quote / reply to.',
      }),
    ),
    attachments: SendMessageAttachmentsSchema,
  },
  {
    additionalProperties: false,
  },
);

export const SendMessageBodySchema = SendMessageBaseBodySchema;

export const SendMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat to send the message to.' }),
});

export const ForwardMessageBodySchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat to forward the message to.' }),
  text: Type.Optional(Type.String({ description: 'Text to be attached to the forwarded message.' })),
});

export const ForwardMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to forward.' }),
});

export const ModifyMessageBodySchema = Type.Object(
  {
    text: Type.String({
      description: `The new textual content of the message. It's recommended not to use HTML or Markdown. To mention a chat participant, write \`@\` followed by their user ID, as found in \`sender_id\` or the chat attendees: it is replaced with their name and notifies them where the provider supports mentions. A provider may still deliver the mention as plain text.`,
    }),
  },
  { additionalProperties: false },
);

export const ModifyMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to modify.' }),
});

export const ReadMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to mark as read.' }),
});

export const PinMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to pin or unpin.' }),
});

export const PinMessageBaseBodySchema = Type.Object(
  {
    pin_status: Type.Boolean({
      description: 'Set to `true` to pin the message in the chat, or `false` to unpin it.',
    }),
  },
  { additionalProperties: false },
);

export const PinMessageBodySchema = PinMessageBaseBodySchema;

export const GetMessagesListParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat to retrieve messages from.' }),
});

export const GetMessagesListQuerySchema = Type.Intersect([
  Type.Object({
    before: Type.Optional(
      IsoDateTimeSchema({
        description: 'Filter to only return messages sent before the given timestamp (exclusive).',
      }),
    ),
    after: Type.Optional(
      IsoDateTimeSchema({
        description: 'Filter to only return messages sent after the given timestamp (exclusive).',
      }),
    ),
    user_id: Type.Optional(Type.String({ description: 'Filter to only return messages sent by the given User.' })),
  }),
  PaginationQueryParamsSchema,
]);

export const GetMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to retrieve.' }),
});

export const DeleteMessageParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'The ID of the Chat where the message is.' }),
  message_id: Type.String({ minLength: 1, description: 'The ID of the Message to delete.' }),
});

export type SendMessageBody<Specifics = object> = Omit<Static<typeof SendMessageBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type SendMessageParams = Static<typeof SendMessageParamsSchema>;
export type ForwardMessageBody = Static<typeof ForwardMessageBodySchema>;
export type ForwardMessageParams = Static<typeof ForwardMessageParamsSchema>;
export type ModifyMessageBody = Static<typeof ModifyMessageBodySchema>;
export type ModifyMessageParams = Static<typeof ModifyMessageParamsSchema>;
export type ReadMessageParams = Static<typeof ReadMessageParamsSchema>;
export type PinMessageParams = Static<typeof PinMessageParamsSchema>;
export type PinMessageBody<Specifics = object> = Static<typeof PinMessageBaseBodySchema> & {
  specifics?: Specifics;
};
export type GetMessagesListParams = Static<typeof GetMessagesListParamsSchema>;
export type GetMessagesListQuery = Static<typeof GetMessagesListQuerySchema>;
export type GetMessageParams = Static<typeof GetMessageParamsSchema>;
export type DeleteMessageParams = Static<typeof DeleteMessageParamsSchema>;
